// Global Instructions Rule Applied!
import React, { useEffect, useState } from 'react'
import { Button, Drawer, Select, Badge, Typography, Space } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import { FilterSpec, FilterOption } from '../lib/listDescriptors.ts'

interface FilterBarProps {
  specs: FilterSpec[]
  /** key → selectable options (from useFilterOptions). */
  options: Record<string, FilterOption[]>
  /** Committed selection (key → selected values). */
  selected: Record<string, string[]>
  /** Commit a new selection (triggers a single list reload). */
  onChange: (next: Record<string, string[]>) => void
}

function countActive(sel: Record<string, string[]>): number {
  return Object.values(sel).filter((v) => Array.isArray(v) && v.length > 0).length
}

/**
 * The list-screen filter control: a "Filters" button (with an active-count badge) that
 * opens a bottom Drawer of multiselects, one per FilterSpec. Edits are staged in a working
 * copy and committed on Apply (one reload, not one per keystroke); Clear empties them.
 * Renders nothing when the module declares no filters.
 */
export function FilterBar({ specs, options, selected, onChange }: FilterBarProps): React.JSX.Element | null {
  const [open, setOpen] = useState(false)
  const [working, setWorking] = useState<Record<string, string[]>>(selected)

  // Re-seed the working copy from the committed selection whenever the drawer opens.
  useEffect(() => {
    if (open) setWorking(selected)
  }, [open, selected])

  if (!specs || specs.length === 0) return null

  const activeCount = countActive(selected)

  const apply = () => {
    const cleaned: Record<string, string[]> = {}
    for (const [k, v] of Object.entries(working)) if (Array.isArray(v) && v.length > 0) cleaned[k] = v
    onChange(cleaned)
    setOpen(false)
  }

  return (
    <>
      <Badge count={activeCount} size="small" offset={[-2, 2]}>
        <Button icon={<FontAwesomeIcon icon={faFilter} />} onClick={() => setOpen(true)}>
          Filters
        </Button>
      </Badge>
      <Drawer
        title="Filters"
        placement="bottom"
        height={Math.min(168 + specs.length * 78, 540)}
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setWorking({})}>Clear</Button>
            <Button type="primary" onClick={apply}>
              Apply
            </Button>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {specs.map((spec) => (
            <div key={spec.key}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {spec.label}
              </Typography.Text>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%', marginTop: 4 }}
                placeholder={`Any ${spec.label.toLowerCase()}`}
                value={working[spec.key] ?? []}
                onChange={(vals) => setWorking((w) => ({ ...w, [spec.key]: vals as string[] }))}
                options={(options[spec.key] ?? []).map((o) => ({ value: o.value, label: o.label }))}
                showSearch
                optionFilterProp="label"
                filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                maxTagCount="responsive"
              />
            </div>
          ))}
        </Space>
      </Drawer>
    </>
  )
}

export default FilterBar
